import { useState, ChangeEvent } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function JsonMinifier() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const minifyJson = (val: string) => {
    if (!val.trim()) {
      setOutput("");
      setError(null);
      return;
    }
    try {
      const parsed = JSON.parse(val);
      setOutput(JSON.stringify(parsed));
      setError(null);
    } catch (e: any) {
      setError(e.message || "Invalid JSON");
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    minifyJson(val);
  };

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="JSON Minifier Online"
      description="Minify your JSON code by removing unnecessary whitespace and characters. Compress JSON for reduced file size."
      h1="JSON Minifier"
      category="JSON Tools"
      categoryPath="/json-tools"
      howToUse={[
        "Paste your formatted JSON into the input area.",
        "The tool automatically minifies the JSON.",
        "Copy the compact JSON output."
      ]}
      faq={[
        { question: "What is JSON minification?", answer: "Minification removes all unnecessary characters (whitespace, newlines) from JSON, reducing its file size." },
        { question: "How to minify JSON?", answer: "Paste your JSON code into our tool and it will automatically compress it into a single line." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Formatted JSON</label>
            <Textarea
              placeholder='{\n  "hello": "world"\n}'
              className="min-h-[400px]"
              value={input}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Minified JSON</label>
              {error ? (
                <span className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Invalid JSON</span>
              ) : output ? (
                <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Valid JSON</span>
              ) : null}
            </div>
            <Textarea
              className="min-h-[400px] bg-slate-50 break-all"
              value={error ? error : output}
              readOnly
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!!error || !output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button onClick={() => { setInput(""); setOutput(""); setError(null); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
